import type { TelemetryReading } from '../../hooks/useTelemetry.ts';
import type { Channel, TimeAxis } from './channels.ts';
import { dangerHigh, readingLevel, stampOf } from './channels.ts';

/* ==========================================================================
   The arithmetic behind every instrument in the kit. Pure functions, no
   React, no DOM — so what a plot claims can be checked without rendering it.

   Three ideas do most of the work:

     SAMPLES    a channel over a window as two aligned arrays plus a cadence,
                with the reading's own timestamp kept so a gap is a gap.
     THE BOX    the y-range a trace is drawn in — the metric's real domain
                where it has one, so an 8% battery can never draw the same
                picture as an 80% one, extended to whichever published limit
                sits just outside the data so the limit is always on screen.
     RUNS       contiguous stretches: of samples (data presence), of a
                categorical field (the state strip), of a threshold breach
                (the segments of a trace that are coloured).
   ========================================================================== */

export interface Samples {
  /** Ascending epoch ms, one per reading in the window. */
  t: number[];
  /** Aligned with `t`. null where the channel had no value in that reading. */
  v: (number | null)[];
  /** Aligned with `t`. The threshold verdict for that reading. */
  lv: ('ok' | 'warn' | 'bad' | 'unknown')[];
  /** Median inter-sample interval, ms. 0 when there are fewer than two. */
  cadence: number;
  /** Beyond this the agent stopped reporting: the trace breaks. */
  gapMs: number;
  /** Extremes of the values actually present. null when the window is empty. */
  lo: number | null;
  hi: number | null;
  /** How many readings the window holds. */
  count: number;
}

const EMPTY: Samples = {
  t: [], v: [], lv: [], cadence: 0, gapMs: Infinity, lo: null, hi: null, count: 0,
};

function median(sorted: readonly number[]): number {
  if (sorted.length === 0) return 0;
  const mid = sorted.length >> 1;
  return sorted.length % 2 === 1
    ? sorted[mid]!
    : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/**
 * Readings are newest-first from the API; a plot needs them ascending. The
 * window is applied here rather than at the fetch so every card, the state
 * strip and the transport bar are looking at the same rows.
 */
export function samplesIn(
  readings: readonly TelemetryReading[],
  channel: Channel,
  from: number,
  to: number,
  axis: TimeAxis,
): Samples {
  const t: number[] = [];
  const v: (number | null)[] = [];
  const lv: ('ok' | 'warn' | 'bad' | 'unknown')[] = [];
  let lo = Infinity;
  let hi = -Infinity;

  for (let i = readings.length - 1; i >= 0; i -= 1) {
    const r = readings[i]!;
    const ts = stampOf(r, axis);
    if (!Number.isFinite(ts) || ts < from || ts > to) continue;
    const raw = channel.pick(r);
    const val = raw == null || Number.isNaN(raw) ? null : raw;
    t.push(ts);
    v.push(val);
    lv.push(readingLevel(channel, r));
    if (val !== null) {
      if (val < lo) lo = val;
      if (val > hi) hi = val;
    }
  }
  if (t.length === 0) return EMPTY;

  const deltas: number[] = [];
  for (let i = 1; i < t.length; i += 1) deltas.push(t[i]! - t[i - 1]!);
  deltas.sort((a, b) => a - b);
  const cadence = median(deltas);

  return {
    t, v, lv, cadence,
    /* 2.5 cadences. One late reading is jitter; two missed in a row is the
       agent having stopped, and drawing a straight line across that is the
       plot inventing telemetry the robot never sent. */
    gapMs: cadence > 0 ? cadence * 2.5 : Infinity,
    lo: lo === Infinity ? null : lo,
    hi: hi === -Infinity ? null : hi,
    count: t.length,
  };
}

/**
 * The worst level anywhere in these samples — a latching annunciator, so a
 * battery that hit 8% an hour ago is still a fact about the window while the
 * cursor stands somewhere healthy.
 */
export function worstIn(s: Samples): 'ok' | 'warn' | 'bad' | 'unknown' {
  let seen = false;
  let worst: 'ok' | 'warn' = 'ok';
  for (const l of s.lv) {
    if (l === 'unknown') continue;
    seen = true;
    if (l === 'bad') return 'bad';
    if (l === 'warn') worst = 'warn';
  }
  return seen ? worst : 'unknown';
}

/**
 * The extreme the window reached at the channel's dangerous end — the reading
 * that earned `worstIn`'s verdict, in the units the trace is drawn in.
 * `null` where the window holds nothing.
 */
export function extremeIn(c: Channel, s: Samples): number | null {
  return dangerHigh(c) ? s.hi : s.lo;
}

/* --------------------------------------------------------------------------
   The box.
   -------------------------------------------------------------------------- */

export interface Box {
  lo: number;
  hi: number;
  /** The box was fitted to the window rather than taken from a real domain. */
  fitted: boolean;
  /** No value at all: the ends are a placeholder and must not be printed as
      an axis. A channel with an empty window and no published domain has no
      honest range to quote, and printing `0.0 – 1.0 GiB` invents one. */
  empty: boolean;
}

export function channelBox(c: Channel, s: Samples): Box {
  const dataLo = s.lo;
  const dataHi = s.hi;

  /* THE BOX IS THE DATA, EXTENDED TO REACH EVERY PUBLISHED LIMIT.
     A published domain is a CLAMP, not the box. Drawing CPU 15–50% against
     0–100 spends half the plot on empty ground and flattens the shape the
     operator came for — §8 tell 6's "a y-axis that does not start where the
     data does". Comparability is not lost, because the thing being compared
     is the lanes inside ONE card and they share one box (see TimeSeriesCard),
     and because every box prints both of its ends.

     But a lane that does not contain its own limit cannot answer the question
     the operator opened it for. This used to admit a limit only within 1.6
     spans of the data, and the arithmetic of that was: on a robot idling at
     31–43 °C the 65 °C warn rule and the 80 °C throttle rule were BOTH off
     the plot, so a temperature lane said nothing at all about how close to
     throttling the machine was. Same for CPU 90%, Link −85 dBm and GNSS 5
     sats. A limit is not "not in play" because the trace is nowhere near it —
     being nowhere near it is the reading, and it can only be read off a plot
     that draws both. So the box always reaches `warn` and `bad`, clamped to
     the published domain, and the trace keeps whatever height that leaves it. */
  if (dataLo === null || dataHi === null) {
    return c.min !== undefined && c.max !== undefined
      ? { lo: c.min, hi: c.max, fitted: false, empty: true }
      : { lo: 0, hi: 1, fitted: true, empty: true };
  }

  /* A circular quantity is the one exception: heading is drawn against the
     whole compass whatever the robot did, because 0 and 360 are the same
     bearing and a fitted box would move north around the plot. */
  if (c.wrap !== undefined && c.min !== undefined && c.max !== undefined) {
    return { lo: c.min, hi: c.max, fitted: false, empty: false };
  }

  let lo = dataLo;
  let hi = dataHi;

  /* Every limit this channel publishes is inside the box, always — otherwise
     the operator judges the trace against the window instead of against the
     rule, and the rule is missing at exactly the moment it matters most.
     A channel judged on a DIFFERENT quantity (disk free, whose limits live on
     disk used %) is the one exception: its numbers are not on this scale and
     pulling the box to 80 would be an axis in the wrong units. */
  for (const lim of [c.warn, c.bad]) {
    if (lim === undefined || c.judge !== undefined) continue;
    if (lim < lo) lo = lim;
    if (lim > hi) hi = lim;
  }

  /* Headroom, but never past a bound the quantity actually has: a speed axis
     that starts at −0.07 m/s is a plot claiming the robot can reverse. */
  const clampLo = (v: number) => (c.min === undefined ? v : Math.max(c.min, v));
  const clampHi = (v: number) => (c.max === undefined ? v : Math.min(c.max, v));

  const span = hi - lo;
  const pad = span === 0 ? Math.max(Math.abs(hi) * 0.1, 0.5) : span * 0.06;
  let outLo = clampLo(lo - pad);
  let outHi = clampHi(hi + pad);

  /* A channel counted in whole units — satellites, dBm — gets whole-unit ends.
     `4.4 sats` on an axis is a quantity the world does not have. */
  if (c.digits === 0) {
    outLo = Math.floor(outLo);
    outHi = Math.ceil(outHi);
  }

  /* An end that landed within 2% of a published bound snaps to it, so a
     battery whose window touched 5.9% draws 0–100 and says so, rather than
     drawing 0.4–100 and printing `0` for a number that is not zero. */
  const snap = (v: number, bound: number | undefined) =>
    bound !== undefined && Math.abs(v - bound) <= (outHi - outLo) * 0.02 ? bound : v;
  outLo = snap(outLo, c.min);
  outHi = snap(outHi, c.max);
  /* `fitted` is a claim about provenance, not about arithmetic: the box was
     derived from this window rather than from a domain the contract publishes,
     and a card that does not say so lets two windows be compared as if their
     axes agreed. It stops being fitted only when both ends landed exactly on
     the published bounds. */
  return {
    lo: outLo,
    hi: outHi,
    fitted: !(outLo === c.min && outHi === c.max),
    empty: false,
  };
}

/** Round-number y ticks inside a box, at most `count` of them. */
export function yTicks(lo: number, hi: number, count: number): number[] {
  if (!(hi > lo)) return [lo];
  const raw = (hi - lo) / Math.max(1, count);
  const mag = 10 ** Math.floor(Math.log10(raw));
  const n = raw / mag;
  const step = (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * mag;
  const out: number[] = [];
  for (let x = Math.ceil(lo / step) * step; x <= hi + step * 1e-6; x += step) {
    out.push(Math.abs(x) < step * 1e-9 ? 0 : x);
  }
  return out;
}

/* --------------------------------------------------------------------------
   Runs.
   -------------------------------------------------------------------------- */

export interface Run<T> {
  start: number;
  end: number;
  value: T;
  /** How many readings the run was built from. */
  n: number;
}

/**
 * Contiguous stretches where the agent was reporting. The space between two
 * runs is a hole in the record and is drawn as one — the presence band exists
 * so an operator can see where coverage is before scrubbing into a hole
 * (`formant-timeline-data-presence-scrubber.png`, §13 tell 21).
 */
export function presenceRuns(
  t: readonly number[],
  gapMs: number,
  cadence: number,
): Run<null>[] {
  if (t.length === 0) return [];
  /* A sample covers the interval up to the next one the agent owed us, so a
     lone reading is one cadence of coverage rather than a zero-width claim. */
  const tail = cadence > 0 ? cadence : 1000;
  const out: Run<null>[] = [];
  let start = t[0]!;
  let prev = t[0]!;
  let n = 1;
  for (let i = 1; i < t.length; i += 1) {
    const cur = t[i]!;
    if (cur - prev > gapMs) {
      out.push({ start, end: prev + tail, value: null, n });
      start = cur;
      n = 0;
    }
    prev = cur;
    n += 1;
  }
  out.push({ start, end: prev + tail, value: null, n });
  return out;
}

/**
 * Runs of a categorical field. A run ends when the value changes OR when the
 * agent went quiet, so a state band never spans a coverage hole — that would
 * claim the robot held a mode through a period nobody heard from it.
 */
export function valueRuns<T>(
  t: readonly number[],
  values: readonly T[],
  gapMs: number,
  cadence: number,
): Run<T>[] {
  if (t.length === 0) return [];
  const tail = cadence > 0 ? cadence : 1000;
  const out: Run<T>[] = [];
  let start = t[0]!;
  let prev = t[0]!;
  let cur = values[0] as T;
  let n = 1;
  for (let i = 1; i < t.length; i += 1) {
    const ts = t[i]!;
    const val = values[i] as T;
    const broke = ts - prev > gapMs;
    if (broke || val !== cur) {
      /* Bands tile: a run ends where the next one begins, so a mode change is
         a seam and not a sliver of bare ground. Across a coverage hole it
         ends one cadence after its last reading instead, because the robot's
         mode through a period nobody heard from it is not a fact we hold. */
      out.push({ start, end: broke ? prev + tail : ts, value: cur, n });
      start = ts;
      cur = val;
      n = 0;
    }
    prev = ts;
    n += 1;
  }
  out.push({ start, end: prev + tail, value: cur, n });
  return out;
}

/**
 * The run a readout should quote at `at` — the same tolerance rule the
 * numeric lanes use, so the mode and the numbers can never disagree about
 * whether this instant has an answer.
 *
 * A run begins at its first sample. A numeric readout, though, quotes the
 * NEAREST sample within one cadence (`sampleAt`), so an instant a hair before
 * the window's first reading has a value on all nine lanes — and, before this
 * existed, no mode: the stack's caption printed `—` while the channel rail
 * two hundred pixels away printed `Idle` for the same cursor. Same tolerance,
 * both directions, one answer.
 */
export function runAt<T>(
  runs: readonly Run<T>[],
  at: number | null,
  cadence: number,
): Run<T> | null {
  if (at === null || runs.length === 0) return null;
  for (const r of runs) if (at >= r.start && at <= r.end) return r;
  const tol = cadence > 0 ? cadence : 1000;
  let best: Run<T> | null = null;
  let bestD = Infinity;
  for (const r of runs) {
    const d = at < r.start ? r.start - at : at - r.end;
    if (d >= 0 && d <= tol && d < bestD) {
      bestD = d;
      best = r;
    }
  }
  return best;
}

/** Indices where a categorical field changed — the transport bar's ticks. */
export function transitions<T>(
  t: readonly number[],
  values: readonly T[],
): { at: number; from: T; to: T }[] {
  const out: { at: number; from: T; to: T }[] = [];
  for (let i = 1; i < t.length; i += 1) {
    const a = values[i - 1] as T;
    const b = values[i] as T;
    if (a !== b) out.push({ at: t[i]!, from: a, to: b });
  }
  return out;
}

/* --------------------------------------------------------------------------
   Path building — where gaps become gaps.
   -------------------------------------------------------------------------- */

export interface Segment {
  d: string;
  level: 'ok' | 'warn' | 'bad' | 'unknown';
}

export interface Trace {
  segments: Segment[];
  /* Samples that ended up alone between two breaks. A one-sample run has no
     line to draw, and dropping it would take a reading off the plot that the
     ledger below is still printing — so it is drawn as the measurement it is.
     Heading is the case that forces this: a rover working a headland wraps
     the compass every few readings, and after the break some runs are one
     sample long. */
  points: { x: number; y: number; level: 'ok' | 'warn' | 'bad' | 'unknown' }[];
}

/**
 * A trace as a list of `M…L…` runs, split at four things and only four:
 *
 *   · a null value            — the channel had no reading
 *   · a coverage gap          — the agent was not reporting
 *   · a threshold crossing    — so the breaching stretch can be coloured
 *   · a wrap of a circular quantity — 359° → 1° is not a fall of 358°
 *
 * Never smoothed, never interpolated across a hole, never closed to a fill.
 *
 * THE WRAP IS A BREAK, NOT A CONNECTOR. For one round this drew the seam
 * crossing: the trace ran out to the top of the lane and re-entered at the
 * bottom at the interpolated instant. That is arithmetically truthful and
 * visually a lie — on a 45px lane it reads as two full-height vertical
 * strokes, i.e. as an enormous excursion, which is exactly the impression a
 * wrap must not give. A compass axis already says that its two ends are the
 * same bearing; a clean break at the seam is read correctly by anyone who
 * knows that, and no one else is operating a rover.
 */
export function buildSegments(
  s: Samples,
  x: (t: number) => number,
  y: (v: number) => number,
  wrap: number | undefined,
): Trace {
  const out: Segment[] = [];
  const points: Trace['points'] = [];
  let pts: string[] = [];
  /* The sample each open run started at, so a run that never got a second
     point can still be drawn as a dot. */
  let head: { x: number; y: number } | null = null;
  let lv: 'ok' | 'warn' | 'bad' | 'unknown' = 'unknown';

  const flush = () => {
    if (pts.length > 1) out.push({ d: pts.join(''), level: lv });
    else if (pts.length === 1 && head !== null) points.push({ ...head, level: lv });
    pts = [];
    head = null;
  };

  for (let i = 0; i < s.t.length; i += 1) {
    const val = s.v[i]!;
    const ts = s.t[i]!;
    if (val === null) {
      flush();
      continue;
    }
    const gap = i > 0 && ts - s.t[i - 1]! > s.gapMs;
    const prev = i > 0 ? s.v[i - 1] : null;
    const wrapped =
      wrap !== undefined && prev != null && Math.abs(val - prev) > wrap / 2;
    const nextLv = s.lv[i]!;

    if (gap || wrapped) {
      flush();
      lv = nextLv;
    } else if (pts.length > 0 && nextLv !== lv) {
      /* Carry the joining point into both segments so the trace has no hole
         at a crossing — the colour changes, the line does not break. */
      pts.push(`L${x(ts).toFixed(1)},${y(val).toFixed(1)}`);
      const carryX = x(s.t[i - 1]!);
      const carryY = y(s.v[i - 1] as number);
      flush();
      lv = nextLv;
      head = { x: carryX, y: carryY };
      pts.push(`M${carryX.toFixed(1)},${carryY.toFixed(1)}`);
    }

    const px = x(ts);
    const py = y(val);
    if (pts.length === 0) head = { x: px, y: py };
    pts.push(`${pts.length === 0 ? 'M' : 'L'}${px.toFixed(1)},${py.toFixed(1)}`);
  }
  flush();
  return { segments: out, points };
}

/** Nearest sample index to a time, or -1. */
export function nearestIndex(t: readonly number[], at: number): number {
  if (t.length === 0) return -1;
  let lo = 0;
  let hi = t.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (t[mid]! < at) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(t[lo - 1]! - at) <= Math.abs(t[lo]! - at)) return lo - 1;
  return lo;
}

/**
 * The sample the readouts should quote at `at` — nearest, but only if it is
 * inside one cadence. Past that the robot was not reporting and the honest
 * answer is that there is no value at this instant, not the last one there was.
 */
export function sampleAt(s: Samples, at: number | null): number {
  if (at === null || s.t.length === 0) return -1;
  const i = nearestIndex(s.t, at);
  const tol = s.cadence > 0 ? s.cadence : 1000;
  return Math.abs(s.t[i]! - at) <= tol ? i : -1;
}

/** The newest sample in the window that actually carried a value, or -1. */
export function lastIndex(s: Samples): number {
  for (let i = s.t.length - 1; i >= 0; i -= 1) if (s.v[i] != null) return i;
  return -1;
}

/**
 * WHICH SAMPLE A READOUT QUOTES, AND THE ONE RULE THAT KEEPS IT HONEST.
 *
 * Two different questions get two different answers:
 *
 *   · a cursor is on the plot — the operator asked about an INSTANT. Answer it
 *     or refuse: past one cadence the robot was not reporting then, and the
 *     last value there was is not an answer to "what was true at 18:07".
 *   · no cursor — the operator asked "what is it now". The honest answer is
 *     the newest reading the window holds, WITH ITS AGE. A robot that last
 *     spoke four cadences ago is the common case (any stale machine, any
 *     backfill), and blanking nine lanes to `—` on it is not honesty, it is
 *     the surface refusing to say what it plainly knows. §13 tell 24: a
 *     "latest" must say how late, not disappear.
 *
 * `stale` is that age flag — the caller renders the value a step down and
 * prints the age once for the surface rather than once per lane.
 */
export interface Quote {
  /** Index into the samples, or -1 when there is nothing to quote. */
  i: number;
  /** The quoted sample is older than one cadence before `edge`. */
  stale: boolean;
}

export function quoteAt(s: Samples, at: number | null, edge: number): Quote {
  if (at !== null) return { i: sampleAt(s, at), stale: false };
  const i = lastIndex(s);
  if (i < 0) return { i: -1, stale: false };
  const tol = s.cadence > 0 ? s.cadence : 1000;
  return { i, stale: edge - s.t[i]! > tol };
}
